import { QRCodeCanvas } from "qrcode.react";
import { FaUser, FaCheckCircle } from "react-icons/fa";
import React, { forwardRef } from "react";

const COLORS = {
  CARD_BG: "#EBBE4E",
  CARD_STRIP: "#C08F32",
  CARD_TEXT: "#6E3E1B",
  SCREEN_BG: "#f7f7f7",
  WHITE: "#ffffff",
};
const styles = {
  cardContainer: {
    display: "flex",
    width: 324,
    height: 204,
    backgroundColor: COLORS.CARD_BG,
    padding: 15,
    borderRadius: 12,
    boxShadow: `0 8px 12px rgba(0,0,0,0.1)`,
    position: "relative",
    overflow: "hidden",
  },
  cornerImage: {
    position: "absolute",
    top: 5,
    right: 5,
    width: 40,
    height: 40,
    borderRadius: "50%",
    opacity: 0.8,
    zIndex: 10,
  },
  qrCodeContainer: {
    position: "absolute",
    bottom: 8,
    right: 8,
    width: 30,
    height: 30,
    borderRadius: 4,
    backgroundColor: COLORS.WHITE,
    padding: 2,
    zIndex: 5,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  absoluteCircle1: {
    position: "absolute",
    top: -20,
    right: -20,
    width: 80,
    height: 80,
    borderRadius: "50%",
    border: `2px solid ${COLORS.CARD_STRIP}50`,
  },
  absoluteCircle2: {
    position: "absolute",
    bottom: -30,
    left: -30,
    width: 100,
    height: 100,
    borderRadius: "50%",
    border: `1px solid ${COLORS.CARD_STRIP}30`,
  },
  leftColumn: {
    width: 85,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 10,
    marginRight: 15,
  },
  profilePicOuter: {
    width: 65,
    height: 65,
    borderRadius: "50%",
    backgroundColor: `${COLORS.CARD_STRIP}30`,
    border: `3px solid ${COLORS.CARD_STRIP}`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  profilePic: {
    width: 50,
    height: 50,
    borderRadius: "50%",
  },
  logoContainer: {
    width: 55,
    height: 55,
    borderRadius: "50%",
    backgroundColor: `${COLORS.CARD_STRIP}`,
    border: `2px solid ${COLORS.CARD_STRIP}`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  rightColumn: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    paddingTop: 8,
    paddingBottom: 8,
    paddingRight: 10,
    position: "relative",
  },
  titleRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 3,
    flexWrap: "wrap",
  },
  title: {
    fontSize: 18,
    fontWeight: "900",
    color: COLORS.CARD_TEXT,
    letterSpacing: 0.5,
  },
  dot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: COLORS.CARD_TEXT,
    margin: "0 8px",
  },
  badge: {
    backgroundColor: COLORS.CARD_STRIP,
    padding: "0px 6px",
    borderRadius: 8,
  },
  badgeText: {
    fontSize: 8,
    color: COLORS.WHITE,
    fontWeight: 700,
    textTransform: "uppercase",
    lineHeight: "10px",
  },
  subtitle: {
    fontSize: 9,
    color: COLORS.CARD_TEXT,
    fontStyle: "italic",
    opacity: 0.8,
    margin: 0,
  },
  divider: {
    height: 1,
    backgroundColor: COLORS.CARD_STRIP,
    margin: "6px 0",
    width: "80%",
    opacity: 0.6,
  },
  label: {
    fontSize: 7,
    color: COLORS.CARD_TEXT,
    fontWeight: 600,
    letterSpacing: 0.8,
    marginBottom: 0.5,
    opacity: 0.8,
  },
  value: {
    fontSize: 12,
    color: COLORS.CARD_TEXT,
    fontWeight: 700,
    margin: 0,
  },
  valueSmall: {
    fontSize: 9,
    color: COLORS.CARD_TEXT,
    fontWeight: 600,
  },
  cardNumber: {
    backgroundColor: COLORS.CARD_STRIP,
    padding: "2px 4px",
    borderRadius: 5,
    width: "fit-content",
    alignSelf: "flex-start",
    display: "inline-flex",
    opacity: 0.9,
  },
  cardNumberText: {
    fontSize: 10,
    color: COLORS.WHITE,
    fontWeight: 700,
    fontFamily: "monospace",
    margin: 0,
    lineHeight: "10px",
  },
  levelAndDOB: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingRight: 40,
  },
  levelBadge: {
    backgroundColor: COLORS.CARD_STRIP,
    padding: "2px 4px",
    borderRadius: 5,
    opacity: 0.8,
    display: "inline-flex",
    height: "14px",
  },
  levelText: {
    fontSize: 8,
    color: COLORS.WHITE,
    fontWeight: 700,
    textTransform: "uppercase",
    lineHeight: "10px",
  },
  verifiedIcon: {
    position: "absolute",
    bottom: 5,
    right: 40,
    width: 20,
    height: 20,
    borderRadius: "50%",
    backgroundColor: COLORS.CARD_STRIP,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    opacity: 0.8,
  },
};

const backCardStyles = {
  backCard: {
    backgroundColor: COLORS.CARD_BG,
    padding: 20,
    borderRadius: 12,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: Math.min(324, window.innerWidth * 0.9),
    height: Math.min(204, window.innerWidth * 0.9 * (204 / 324)),
  },
  scrollView: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    textAlign: "center",
  },
  paragraph: {
    color: COLORS.CARD_TEXT,
    fontSize: 10,
    lineHeight: "14px",
    marginBottom: 8,
    maxWidth: "90%",
  },
  title: {
    color: COLORS.CARD_TEXT,
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 6,
  },
  text: {
    color: COLORS.CARD_TEXT,
    fontSize: 10,
    marginBottom: 4,
  },
}

const DonorCard = forwardRef((props,ref) => {

      const { receiptDetails } = props;
  return (
    <div ref={ref} className="d-flex  flex-column flex-md-row gap-3 align-items-center justify-content-md-center p-4">
      <div style={styles.cardContainer}>
        <img
          src="https://www.kanpurfolk.in/WhatsApp%20Image%202025-05-06%20at%202.37.12%20PM%20(1).jpeg"
          alt="Corner"
          style={styles.cornerImage}
        />

        <div style={styles.qrCodeContainer}>
          <QRCodeCanvas
            value={receiptDetails.patronship_no}
            size={25}
            fgColor={COLORS.CARD_TEXT}
            bgColor={COLORS.WHITE}
            level="L"
          />
        </div>

        <div style={styles.absoluteCircle1}></div>
        <div style={styles.absoluteCircle2}></div>

        <div style={styles.leftColumn}>
          <div style={styles.profilePicOuter}>
            {receiptDetails?.profile_pic ? (
              <img
                src={receiptDetails.profile_pic}
                alt="Profile"
                style={styles.profilePic}
              />
            ) : (
              <FaUser size={30} color={COLORS.CARD_TEXT} />
            )}
          </div>

          <div style={styles.logoContainer}>
            <img
              src="/WhatsApp Image 2025-05-06 at 2.34.06 PM (1).jpeg"
              alt="Logo"
              style={{ width: 50, height: 50 }}
            />
          </div>
        </div>

        <div style={styles.rightColumn}>
          <div>
            <div style={styles.titleRow}>
              <span style={styles.title}>FOLK</span>
              <div style={styles.dot} />
              <div style={styles.levelBadge}>
                <span style={styles.badgeText}>PATRON</span>
                {/* <span style={styles.levelText}>PATRON</span> */}
              </div>
            </div>
            <p style={styles.subtitle}>Family of Lord Krishna</p>
            <div style={styles.divider} />
          </div>

          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div>
              <p style={styles.label}>MEMBER NAME</p>
              <p style={styles.value}>
                {receiptDetails?.full_name || "Loading..."}
              </p>
            </div>

            <div style={{ marginBottom: 2 }}>
              <p style={styles.label}>CARD NUMBER</p>
              <div style={styles.cardNumber}>
                <p style={styles.cardNumberText}>
                  {receiptDetails?.patronship_no || "Loading..."}
                </p>
              </div>
            </div>

            <div style={styles.levelAndDOB}>
              <div className="mb-4" style={{ flex: 1}}>
                <p style={styles.label}>LEVEL</p>
                <div  style={styles.levelBadge}>
                  <p  style={styles.levelText}>
                    {receiptDetails?.patronship_level || "Loading..."}
                  </p>
                </div>
              </div>
              <div className="mb-3" style={{ flex: 1} }>
                <p style={styles.label}>DOB</p>
                <p style={styles.valueSmall}>
                  {receiptDetails?.date_of_birth || "Loading..."}
                </p>
              </div>
            </div>
          </div>

          <div style={styles.verifiedIcon}>
            <FaCheckCircle size={12} color={COLORS.WHITE} />
          </div>
        </div>
      </div>
      <CardBack />
    </div>
  );
})

const CardBack = () => {
  return (
    <div style={backCardStyles.backCard}>
      <div style={backCardStyles.scrollView}>
        <p style={backCardStyles.paragraph}>
          Whatever you do, whatever you eat, whatever you offer or give away,
          and whatever austerities you perform – do that, O son of Kunti, as an
          offering to Me.
        </p>

        <p style={backCardStyles.paragraph}>
          Thank you for your patronship in mission of spreading Krishna
          consciousness.
        </p>

        <p style={backCardStyles.title}>
          FOLK - Family of Lord Krishna (ISKCON)
        </p>
        <p style={backCardStyles.text}>+91 7080007066</p>
        <p style={backCardStyles.text}>www.folk.org.in</p>
      </div>
    </div>
  );
};

export default DonorCard;
